import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Chip, Skeleton } from '@mui/material';
import { formatTimeToIST, formatDateToIST } from '../../../utils/dateUtils';
import CopyButton from '../common/CopyButton';
import { formatTradeValue } from './tradeHistoryFormatters';

// Order ID column: left-aligned and only as wide as its content. A class, since
// the table-wide centring rule outranks the cell's own `align` prop.
const ORDER_ID_CELL = 'cell-order-id';

// Loading-placeholder widths per column (not the column widths), sized like real
// content so the layout barely moves when data arrives.
const SKELETON_COLUMNS = [
  { width: 130, orderId: true }, // Order ID + copy button
  { width: 110 },                // Symbol
  { width: 72 },                 // Date
  { width: 60 },                 // Time
  { width: 24 },                 // Quantity
  { width: 48 },                 // Price
  { width: 64 },                 // Value
  { width: 40, chip: true },     // Buy/Sell
];
const SKELETON_ROW_COUNT = 12;

/** Trade rows as a sticky-header table, used at `sm` and above. */
const TradeHistoryTable = ({ trades, loading = false }) => (
                <Table
                  stickyHeader
                  aria-label="sticky table"
                  aria-busy={loading || undefined}
                  size="small"
                  sx={{
                    tableLayout: 'auto',
                    // Centred values that never wrap (no "19-10-" / "2026"); 8px
                    // cell padding (16px at the outer edges) so all eight columns
                    // fit narrower panes before the container scrolls sideways.
                    '& th, & td': { textAlign: 'center', whiteSpace: 'nowrap', px: 1 },
                    '& th:first-of-type, & td:first-of-type': { pl: 2 },
                    '& th:last-of-type, & td:last-of-type': { pr: 2 },
                    [`& .${ORDER_ID_CELL}`]: {
                      textAlign: 'left',
                      // With auto layout a 1% column shrinks to its content;
                      // the other columns share the rest of the row.
                      width: '1%',
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  <TableHead sx={{ 
                    '& th': { 
                      position: 'sticky', 
                      top: 0,
                      zIndex: 10,
                      whiteSpace: 'nowrap',
                      boxShadow: '0px 2px 2px -1px rgba(0,0,0,0.1)'
                    } 
                  }}>
                    <TableRow>
                      <TableCell className={ORDER_ID_CELL}>Order ID</TableCell>
                      <TableCell>Symbol</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Value</TableCell>
                      <TableCell>Buy/Sell</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading && Array.from({ length: SKELETON_ROW_COUNT }, (_, rowIndex) => (
                      <TableRow key={`skeleton-${rowIndex}`}>
                        {SKELETON_COLUMNS.map(({ width, chip, orderId }, colIndex) => (
                          <TableCell key={colIndex} className={orderId ? ORDER_ID_CELL : undefined}>
                            {/* Skeletons are blocks, so text-align can't place them — margins do. */}
                            {chip
                              ? <Skeleton variant="rounded" width={width} height={24} sx={{ borderRadius: 4, mx: orderId ? 0 : 'auto' }} />
                              : <Skeleton variant="text" width={width} sx={{ mx: orderId ? 0 : 'auto' }} />}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    {!loading && trades.map((trade) => (
                      <TableRow hover tabIndex={-1} key={trade.trade_id || trade.id}>
                        <TableCell className={ORDER_ID_CELL}>
                          {/* Tabular digits give every ID of the same length the same
                              width, so the copy icons line up down the column. */}
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                            {trade.order_id}
                            {trade.order_id && <CopyButton value={trade.order_id} label="order ID" />}
                          </Box>
                        </TableCell>
                        <TableCell>{trade.trading_symbol}</TableCell>
                        <TableCell>{formatDateToIST(trade.created_at)}</TableCell>
                        <TableCell>{formatTimeToIST(trade.created_at)}</TableCell>
                        <TableCell>{trade.fill_quantity}</TableCell>
                        <TableCell>{trade.fill_price}</TableCell>
                        <TableCell>{formatTradeValue(trade) ?? '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={trade.buy_sell === 0 ? "Buy" : "Sell"}
                            color={trade.buy_sell === 0 ? "success" : "error"}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
);

export default TradeHistoryTable;
